import { TaskDetails, formatPlanChain } from '../../utils/taskHelper'
import {
  DuedateSummary,
  getTaskProgressText,
  MapPinIcon,
  Mtds,
  ScalesIcon,
  VirusIcon
} from 'prove-common'
import { useMemo } from 'react'
import { Link } from 'react-router'

export type TaskCardProps = {
  id: string
  details: TaskDetails
  isCompleted: boolean
  isGrouped?: boolean
  onClick?: (id: string) => void
  to?: string
}

const getCardColor = (isCompleted: boolean, hasDuedate: boolean) => {
  if (isCompleted) return 'success'
  if (hasDuedate) return 'info'
  return undefined
}

const getAnalysisLabel = (
  analysis: string | undefined,
  isGrouped: boolean | undefined,
  analysisCount: number
) => {
  if (isGrouped !== true) return analysis
  return analysisCount > 1 ? `${analysisCount} analyser` : '1 analyse'
}

const getApprovalStatusTag = (
  analysisTypeApprovalStatus: TaskDetails['analysisTypeApprovalStatus'],
  approvalComment: TaskDetails['approvalComment']
) => {
  if (analysisTypeApprovalStatus === 'awaiting') {
    return (
      <Mtds.Tag
        data-color='warning'
        data-tooltip='Avventer godkjenning fra laboratoriet'
      >
        Avventer
      </Mtds.Tag>
    )
  }

  if (analysisTypeApprovalStatus === 'declined') {
    return (
      <Mtds.Tag data-color='danger' data-tooltip={approvalComment ?? 'Avvist'}>
        Avvist
      </Mtds.Tag>
    )
  }

  return null
}

const ApprovalStatusAlert = ({
  analysisTypeApprovalStatus,
  approvalComment
}: {
  analysisTypeApprovalStatus: TaskDetails['analysisTypeApprovalStatus']
  approvalComment: TaskDetails['approvalComment']
}) => {
  if (analysisTypeApprovalStatus === 'awaiting') {
    return (
      <Mtds.Alert data-color='warning'>
        <Mtds.Heading>
          Denne kombinasjonen avventer godkjenning fra laboratoriet
        </Mtds.Heading>
      </Mtds.Alert>
    )
  }

  if (analysisTypeApprovalStatus === 'declined') {
    return (
      <Mtds.Alert data-color='danger'>
        <Mtds.Heading>Avvist</Mtds.Heading>
        <p>{approvalComment ?? 'Ingen avvisningsgrunn oppgitt.'}</p>
      </Mtds.Alert>
    )
  }

  return null
}

export const TaskCard = ({
  id,
  details,
  isCompleted,
  isGrouped,
  onClick,
  to
}: TaskCardProps) => {
  const {
    product,
    matrix,
    matrixAmount,
    analysis,
    sampntTypeName,
    countSubSamples,
    samplesTaken,
    samplesToTake,
    duedate,
    analysisTypeApprovalStatus,
    approvalComment
  } = details

  const analysisCount =
    details?.analysis?.split('\n').filter(Boolean).length ?? 0
  const analysisLabel = getAnalysisLabel(analysis, isGrouped, analysisCount)
  const progressText = getTaskProgressText(samplesTaken, samplesToTake)

  const planChain = useMemo(
    () => formatPlanChain(details?.planChain),
    [details?.planChain]
  )

  const className = duedate || isCompleted ? 'bg-background-tinted' : undefined
  const color = getCardColor(isCompleted, duedate != null)
  const approvalStatusTag = getApprovalStatusTag(
    analysisTypeApprovalStatus,
    approvalComment
  )
  const approvalStatusAlert = (
    <ApprovalStatusAlert
      analysisTypeApprovalStatus={analysisTypeApprovalStatus}
      approvalComment={approvalComment}
    />
  )
  const cardContent = (
    <Mtds.Grid data-gap='3'>
      {planChain != null && <Mtds.Muted>{planChain}</Mtds.Muted>}
      <Mtds.Flex data-justify='space-between' data-align='center'>
        <Mtds.Heading data-size='xs' className='mr-auto'>
          {product ?? matrix}
          {product != null && matrix != null && ` > ${matrix}`}
        </Mtds.Heading>
        <Mtds.Flex data-gap='2' data-align='center'>
          {isGrouped !== true && <Mtds.Muted>{analysisLabel}</Mtds.Muted>}
          {approvalStatusTag}
        </Mtds.Flex>
      </Mtds.Flex>

      <Mtds.Flex data-gap='3' data-wrap data-align='center'>
        {sampntTypeName != null && (
          <Mtds.Muted>
            <span className='inline-flex items-center gap-1'>
              <MapPinIcon aria-hidden />
              {sampntTypeName}
            </span>
          </Mtds.Muted>
        )}
        {matrixAmount != null && (
          <Mtds.Muted>
            <span className='inline-flex items-center gap-1'>
              <ScalesIcon aria-label='Mengde prøvemateriale' />
              {matrixAmount}
            </span>
          </Mtds.Muted>
        )}
        {isGrouped === true && (
          <Mtds.Muted>
            <span className='inline-flex items-center gap-1'>
              <VirusIcon aria-hidden />
              {analysisLabel}
            </span>
          </Mtds.Muted>
        )}
      </Mtds.Flex>
      <Mtds.Divider data-gap='0' />
      <Mtds.Flex data-nowrap data-align='center' data-justify='space-between'>
        <Mtds.Progress value={samplesTaken} max={samplesToTake} />
        <Mtds.Muted data-nowrap>
          <strong>{progressText}</strong>{' '}
          {countSubSamples ? 'delprøver' : 'prøveposer'}
        </Mtds.Muted>
      </Mtds.Flex>
      {duedate != null && (
        <Mtds.Muted>
          <DuedateSummary duedate={duedate} />
        </Mtds.Muted>
      )}
    </Mtds.Grid>
  )

  if (to != null) {
    return (
      <Mtds.Card as={Link} to={to} className={className} data-color={color}>
        {approvalStatusAlert}
        {cardContent}
      </Mtds.Card>
    )
  }

  return (
    <Mtds.Card
      as='button'
      type='button'
      className={className}
      onClick={() => onClick?.(id)}
      data-color={color}
    >
      {approvalStatusAlert}
      {cardContent}
    </Mtds.Card>
  )
}
